import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";

// ✅ Safe text helper
const safeText = (v) =>
  v === null || v === undefined || String(v).trim() === ""
    ? "No details provided"
    : String(v);

export default function BulkOrders() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedOrder, setSelectedOrder] = useState(null);

  const LIMIT = 10;

  // 🔁 Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${baseurl}/api/bulk-request?page=${page}&limit=${LIMIT}&q=${debouncedSearch}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setData(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / LIMIT);

  // ✅ Requirement fallback
  const getRequirement = (order) => {
    return safeText(
      order?.description ||
        order?.requirement ||
        order?.message ||
        order?.details
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bulk Orders
            </h1>
            <p className="text-sm text-gray-500">
              Manage all bulk requests
            </p>
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, email, phone..."
            className="w-72 px-4 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">

          {loading ? (
            <div className="p-10 text-center text-gray-500">
              Loading bulk requests...
            </div>
          ) : data.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              No bulk requests found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">

                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-5 py-3 text-left">Customer</th>
                    <th className="px-5 py-3 text-left">Contact</th>
                    <th className="px-5 py-3 text-left">Company</th>
                    <th className="px-5 py-3 text-center">Files</th>
                  </tr>
                </thead>

                <tbody>
                  {data.map((item) => (
                    <tr
                      key={item._id}
                      onClick={() => setSelectedOrder(item)}
                      className="border-t cursor-pointer hover:bg-blue-50 transition"
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold">
                          {safeText(item.fullName)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {safeText(item.city)}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div>{safeText(item.phone)}</div>
                        <div className="text-xs text-gray-500">
                          {safeText(item.email)}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        {safeText(item.company)}
                      </td>

                      <td className="px-5 py-4 text-center">
                        {item.files?.length || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Prev
            </button>

            <div className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </div>

            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ===================== */}
      {/* 🔥 CENTER MODAL CARD */}
      {/* ===================== */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">

          {/* BACKDROP */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedOrder(null)}
          />

          {/* MODAL */}
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 mx-4 animate-fadeIn">

            {/* HEADER */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-gray-900">
                Order Details
              </h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-black text-lg"
              >
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div className="max-h-[70vh] overflow-y-auto pr-1">

              {/* CUSTOMER */}
              <div className="mb-4">
                <h3 className="text-xs text-gray-500 uppercase">
                  Customer
                </h3>
                <p className="font-semibold text-lg">
                  {safeText(selectedOrder.fullName)}
                </p>
                <p className="text-sm text-gray-500">
                  {safeText(selectedOrder.city)}
                </p>
              </div>

              {/* CONTACT */}
              <div className="mb-4">
                <h3 className="text-xs text-gray-500 uppercase">
                  Contact
                </h3>
                <p>{safeText(selectedOrder.phone)}</p>
                <p className="text-sm text-gray-500 break-all">
                  {safeText(selectedOrder.email)}
                </p>
              </div>

              {/* COMPANY */}
              <div className="mb-4">
                <h3 className="text-xs text-gray-500 uppercase">
                  Company
                </h3>
                <p>{safeText(selectedOrder.company)}</p>
              </div>

              {/* REQUIREMENT */}
              <div className="mb-4">
                <h3 className="text-xs text-gray-500 uppercase">
                  Requirement Details
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {getRequirement(selectedOrder)}
                </p>
              </div>

              {/* FILES */}
              <div>
                <h3 className="text-xs text-gray-500 uppercase mb-2">
                  Files
                </h3>

                {selectedOrder.files?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedOrder.files.map((file, i) => (
                      <a
                        key={i}
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
                      >
                        📄 {file.originalname}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">
                    No files uploaded
                  </p>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}